import {Injectable} from '@angular/core';
import {HttpService} from '../core/http.service';
import {StorageService} from '../core/storage.service';
import {BehaviorSubject, lastValueFrom, Subject, Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {Customer} from '../../interfaces/customers/customer';
import {CustomerFactory} from '../../factories/customers/customer-factory';
import {db} from '../../tables/db.service';
import {ReRelationTypesTable} from '../../tables/relatives/re-relation-types.table'
import {DateTime} from 'luxon';

@Injectable({
  providedIn: 'root'
})
export class CustomersService {
  private static readonly _storageKey: string = 'selected-customer';
  private readonly _customers$: Subject<Customer[]>;
  private readonly _selectedCustomer$: BehaviorSubject<Customer>;

  private _selectedCustomer: Customer = null;

  /**
   *
   * @param httpService
   * @param reRelationTypesTable
   * @param storageService
   */
  constructor(private httpService: HttpService,
              private reRelationTypesTable: ReRelationTypesTable,
              private storageService: StorageService) {
    this._customers$ = new Subject<Customer[]>();
    this._selectedCustomer$ = new BehaviorSubject<Customer>(this._selectedCustomer);
  }

  /**
   *
   */
  public async init(): Promise<void> {
    await db.customers.toCollection().sortBy('fullName');
    this._selectedCustomer = await this.storageService.getItem(CustomersService._storageKey);
    this._selectedCustomer$.next(this._selectedCustomer);
  }

  get customers$(): Subject<Customer[]> {
    return this._customers$;
  }

  get selectedCustomer(): Customer {
    return this._selectedCustomer;
  }

  get selectedCustomer$(): BehaviorSubject<Customer> {
    return this._selectedCustomer$;
  }

  /**
   *
   * @param customer
   */
  public async selectCustomer(customer: Customer): Promise<void> {
    this._selectedCustomer = customer;
    await this.storageService.setItem(CustomersService._storageKey, customer);
    this._selectedCustomer$.next(this._selectedCustomer);
  }

  /**
   *
   * @param customerId
   */
  public async getCustomer(customerId: number): Promise<Customer> {
    return db.customers.get(customerId);
  }

  /**
   *
   */
  public async getCustomers(serviceFacilityId: number = null): Promise<Customer[]> {
    if(serviceFacilityId) {
      return db.customers.where('serviceFacilityId').equals(serviceFacilityId).sortBy('fullName');
    }
    return db.customers.toCollection().sortBy('fullName');
  }

  /**
   *
   * @param customerIds
   */
  public async updateLastDataUpdate(customerIds: number[]): Promise<void> {
    if(customerIds.length <= 0) {
      return;
    }

    const lastDataUpdate = DateTime.local().toISO();
    await db.customers.where('id').anyOf(customerIds).modify({ lastDataUpdate });

    if(this._selectedCustomer && customerIds.some(customerId => customerId === this._selectedCustomer.id)) {
      const selectedCustomer = await this.getCustomer(this._selectedCustomer.id);
      await this.selectCustomer(selectedCustomer);
    }
  }

  /**
   *
   * @param query
   */
  public async loadCustomers(query: {
    serviceFacilityIds?: number[],
    onlyRelevant?: boolean,
  }): Promise<Customer[]> {
    let customers: Customer[] = [];

    const _query: any = {
      with_primary_nurse: '1',
      with_social_insurance: '1',
      with_doctors: '1',
      with_relatives: '1',
      next_customer_activities: '6',
      previous_customer_activities: '6'
    }

    if(query.onlyRelevant) {
      _query.only_relevant = '1';
    }
    else {
      _query.service_facility_ids = query.serviceFacilityIds;
    }

    try {
      const reRelationTypes = await this.reRelationTypesTable.getAll();

      customers = await lastValueFrom(this.httpService.get<any[]>({
        url: ['customers'],
        query: _query
      }, { timeout: 30000 }).pipe(
        map(_customers => {
          // TODO: Better solution from api
          const customers = _customers.map(_customer => {
            for(let reRelative of _customer.re_relatives) {
              reRelative.re_relation_type =
                reRelationTypes.find(
                  _reRelationType => _reRelationType.id === reRelative.re_relation_type_id
                );
            }
            return _customer;
          });
          return CustomerFactory.fromArray(customers);
        })
      ));

      const customerIds = customers.map(customer => customer.id);
      const existingCustomers = await db.customers.bulkGet(customerIds);
      customers = customers.map((customer, index) => {
        customer.lastDataUpdate = existingCustomers[index]?.lastDataUpdate ?? null;
        return customer;
      });

      await db.transaction('rw', db.customers, async () => {
        if(query.serviceFacilityIds) {
          await db.customers.where('serviceFacilityId').anyOf(query.serviceFacilityIds).delete();
        }
        await db.customers.bulkDelete(customerIds);
        await db.customers.bulkAdd(customers);
      });

      if(!await this.checkIfCustomerExists(this._selectedCustomer)) {
        await this.selectCustomer(null);
      }

      this._customers$.next(await this.getCustomers());
    }
    catch(e) {
      throw e;
    }

    return customers;
  }

  /**
   *
   */
  public async loadCustomer(customerId: number): Promise<Customer> {
    try {
      const reRelationTypes = await this.reRelationTypesTable.getAll();
      const existingCustomer = await db.customers.get(customerId);

      const customer  = await lastValueFrom(this.httpService.get<any>({
        url: ['customers', customerId.toString()],
        query: {
          with_primary_nurse: '1',
          with_social_insurance: '1',
          with_doctors: '1',
          with_relatives: '1',
          next_customer_activities: '6',
          previous_customer_activities: '6'
        }
      }).pipe(map((_customer) => {
        for(let reRelative of _customer.re_relatives) {
          reRelative.re_relation_type =
            reRelationTypes.find(
              _reRelationType => _reRelationType.id === reRelative.re_relation_type_id
            );
        }
        return CustomerFactory.fromObject(_customer);
      })));

      customer.lastDataUpdate = existingCustomer?.lastDataUpdate ?? null;

      await db.transaction('rw', db.customers, async () => {
        await db.customers.put(customer);
      });

      return await db.customers.get(customer.id);
    }
    catch (e) {
      throw e;
    }
  }

  /**
   *
   * @param serviceFacilityId
   */
  public async loadCustomersIfNoCached(serviceFacilityId: number): Promise<Customer[]> {
    const customers = await this.getCustomers(serviceFacilityId);
    if(customers.length > 0) {
      return customers;
    }
    await this.loadCustomers({
      serviceFacilityIds: [serviceFacilityId]
    });
    return await this.getCustomers(serviceFacilityId);
  }

  /**
   *
   * @param customerId
   */
  public async selectCustomerById(customerId: number): Promise<void> {
    const customer = await this.getCustomer(customerId);
    await this.selectCustomer(customer);
  }

  /**
   *
   */
  public async clear(): Promise<void> {
    await db.customers.clear();
    await this.selectCustomer(null);
    this._customers$.next([]);
  }

  /**
   *
   * @param customerIds
   */
  public async checkIfCustomerIdsExists(customerIds: number[]): Promise<boolean> {
    const customers = await db.customers.where('id').anyOf(customerIds).toArray();
    return customers.length === customerIds.length;
  }

  /**
   *
   * @param customer
   * @private
   */
  private async checkIfCustomerExists(customer: Customer): Promise<boolean> {
    const customers = await db.customers.toArray();
    return customers.some(_customer => _customer.id === customer?.id);
  }
}
